import { useState } from 'react';
import { useSelector } from 'react-redux';
import { Box } from '@chakra-ui/react';

import { toSentenceCase } from 'utils/utils';
import { Field } from 'formik';
import ZoomVisibleScopeField from './formFields/ZoomVisibleScopeField';
import updateServiceProfiles from 'API/ServiceProfiles/updateServiceProfiles';
import createServiceProfile from 'API/ServiceProfiles/createServiceProfile';
import Alert from 'components/v4/Alert';
import AlertDescription from 'components/v4/AlertDescription';
import AlertIcon from 'components/v4/AlertIcon';
import LoadingIndicator from 'components/v4/LoadingIndicator';
import { validationSchemaTPM } from './schema';
import useFieldValidation from 'hooks/useFieldValidation';
import FormBase from '@/components/forms/new/FormBase';
import BreadcrumbTrail from 'components/BreadcrumbTrail';
import useGetCurrentWholesaler from '@/queries/wholesaler/useGetCurrentWholesaler';
import { resolveWholesalerProfileName } from './serviceProfileName';

const Zoom = ({ mode, data, handleBackButton, level, visibleScopeOptions }) => {
    const {
        currentWholesaler: wholesalerID,
        currentPartner: partnerID,
        companiesAndPartners,
    } = useSelector((state) => state.navigationLists);
    const { data: currentWholesalerData } = useGetCurrentWholesaler();
    const { requiredScope } = useSelector((state) => state.login);
    const { darkMode } = useSelector((state) => state.settings);

    const [error, setError] = useState(null);

    const [isLoading, setIsLoading] = useState(false);

    // Formik initial values - compute name based on mode and level
    const getInitialName = () => {
        if (data?.name) return data.name;
        if (level === 'partner') {
            const partnerName = companiesAndPartners.find(
                ({ id }) => id === partnerID,
            )?.name;
            return `${partnerName} - Zoom Partner Profile`;
        } else {
            return resolveWholesalerProfileName(
                currentWholesalerData?.name,
                'Zoom',
            );
        }
    };

    const initialValues = {
        name: getInitialName(),
        serviceType: 'ZoomPEX',
        partnerID:
            level === 'partner' ? (data?.partnerID ?? partnerID) : undefined,
        wholesalerID:
            level === 'partner'
                ? undefined
                : (data?.wholesalerID ?? wholesalerID),
        visibleScope: data?.visibleScope ?? (level === 'partner' ? 20 : 60),
    };

    const { validatefn } = useFieldValidation(
        initialValues,
        validationSchemaTPM,
    );

    if (isLoading) {
        return <LoadingIndicator />;
    }

    // Formik submit handler
    const finalFormSubmit = async (values, actions) => {
        setIsLoading(true);
        setError(null);
        try {
            if (!validatefn(values)) {
                setError({
                    response: {
                        data: {
                            message:
                                'There are some validation errors in the form. Please check and try again.',
                        },
                    },
                });
                actions.setSubmitting(false);
                setIsLoading(false);
                return;
            }
            if (mode === 'EDIT') {
                await updateServiceProfiles({
                    type: 'Zoom',
                    payload: { ...data, ...values },
                    level,
                    scope: requiredScope,
                });
                handleBackButton();
            } else {
                await createServiceProfile({
                    type: 'Zoom',
                    payload: values,
                    level,
                    scope: requiredScope,
                });
                handleBackButton();
            }
        } catch (e) {
            setError(
                e?.response?.data?.message ||
                    e?.message ||
                    'An error occurred. Please try again later.',
            );
        } finally {
            setIsLoading(false);
            actions.setSubmitting(false);
        }
    };
    const breadCrumbTrail = [
        { label: 'Services', path: ['', { index: 0 }] },
        { label: 'Configurations', path: '' },
    ];
    return (
        <>
            <BreadcrumbTrail
                breadCrumbTrail={breadCrumbTrail}
                handleBackButton={handleBackButton}
            />
            <FormBase
                header={`${toSentenceCase(mode)} ${initialValues.name}`}
                subTitleHeader={true}
                backLink={['/service-profiles', { state: { index: 0 } }]}
                ufBackBtn="builderBackBtn"
                ufSaveBtn="builderSaveBtn"
                initialValues={initialValues}
                finalFormSubmit={finalFormSubmit}
                customClick={true}
                onBack={handleBackButton}
                isLoading={isLoading}
                enableReinitialize={true}>
                {({ errors, touched }) =>
                    [
                        <Field
                            key="visibleScope"
                            errors={errors}
                            touched={touched}
                            name="visibleScope"
                            darkMode={darkMode}
                            options={visibleScopeOptions}
                            component={ZoomVisibleScopeField}
                        />,
                        error && (
                            <Box paddingBottom="0.5rem" key="error-box-zoom">
                                <Alert
                                    key="error"
                                    status="error"
                                    mt={4}
                                    marginBottom="1rem">
                                    <AlertIcon />
                                    <AlertDescription>{error}</AlertDescription>
                                </Alert>
                            </Box>
                        ),
                    ].filter(Boolean)
                }
            </FormBase>
        </>
    );
};

export default Zoom;
